import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PHASES } from '../utils/data';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle } = FiIcons;

export const MetabolicTracker = ({ elapsedHours }) => {
  // Memoize phase calculations
  const { currentPhase, phaseProgress, phaseIndex } = useMemo(() => {
    const phase = PHASES.find(p => elapsedHours >= p.start && elapsedHours < p.end) || PHASES[PHASES.length - 1];
    const phaseDuration = phase.end - phase.start;
    const progress = phaseDuration > 100 ? 100 : Math.min(((elapsedHours - phase.start) / phaseDuration) * 100, 100);
    const index = PHASES.indexOf(phase);
    
    return {
      currentPhase: phase,
      phaseProgress: progress,
      phaseIndex: index
    };
  }, [elapsedHours]);

  return (
    <div className="w-full max-w-md">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPhase.title}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.3 }}
          className="relative overflow-hidden rounded-[2.5rem] bg-white shadow-2xl shadow-slate-200 border border-slate-100"
        >
          {/* Top Visual Section - Optimized Image Loading */}
          <div className="h-56 w-full relative overflow-hidden bg-slate-100">
            <img
              src={currentPhase.image}
              alt={currentPhase.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
            <div className="absolute top-6 right-6">
              <span className="bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-900 border border-white/50 shadow-sm">
                Stage {phaseIndex + 1}
              </span>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8 -mt-12 relative bg-white rounded-t-[2.5rem]">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-2xl shadow-sm">
                <SafeIcon icon={currentPhase.icon} className="text-2xl" />
              </div>
              <div>
                <h3 className="text-slate-900 font-black text-2xl tracking-tighter leading-none">
                  {currentPhase.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-emerald-500 text-[11px] font-black uppercase tracking-widest">Active</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-400 text-[11px] font-bold">
                    {currentPhase.start}-{currentPhase.end > 100 ? '∞' : currentPhase.end}H
                  </span>
                </div>
              </div>
            </div>

            <p className="text-slate-600 text-sm leading-relaxed font-medium mb-8">
              {currentPhase.desc}
            </p>

            {/* Body Details */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Inside your body</h4>
                <div className="h-px flex-1 bg-slate-100 ml-4" />
              </div>

              <ul className="space-y-4">
                {currentPhase.bodyDetails.map((detail, idx) => (
                  <li key={idx} className="flex gap-4 items-start group">
                    <div className="mt-1 flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-lg group-hover:scale-110 transition-transform" />
                    </div>
                    <span className="text-slate-500 text-[13px] leading-snug font-medium">
                      {detail}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Progress Bar */}
            <div className="mt-10 pt-6 border-t border-slate-50">
              <div className="flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                <span>Phase Completion</span>
                <span className="text-emerald-500">{Math.round(phaseProgress)}%</span>
              </div>
              <div className="h-2 bg-slate-50 rounded-full overflow-hidden border border-slate-100 p-0.5">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${phaseProgress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full bg-emerald-500 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};